// src/components/customers/JobSpecBadge.jsx

/**
 * JobSpecBadge Component
 *
 * A component for displaying job specifications as styled badges.
 * Shows various job characteristics like floor conditions, requirements, etc.
 *
 * @param {Object} props - Component props
 * @param {string} props.label - Badge label text
 * @param {boolean} props.value - Whether the specification is true/false
 * @param {string} props.type - Badge type for styling ('success', 'warning', 'info', 'danger')
 * @param {string} props.icon - Optional icon component or SVG
 * @param {string} props.trueText - Text to show when value is true
 * @param {string} props.falseText - Text to show when value is false
 * @param {boolean} props.showWhenFalse - Whether to show badge when value is false
 * @param {string} props.className - Additional CSS classes
 */
const JobSpecBadge = ({
  label,
  value,
  type = "info",
  icon,
  trueText,
  falseText,
  showWhenFalse = false,
  className = "",
}) => {
  // Determine display text
  const displayText = value
    ? trueText || label || "Yes"
    : falseText || `No ${label}` || "No";

  // Don't render if value is false and showWhenFalse is false
  if (!value && !showWhenFalse) {
    return null;
  }

  // Determine badge styling based on type and value
  const getBadgeStyle = () => {
    if (!value) {
      return "bg-gray-100 text-gray-600 border-gray-200";
    }

    switch (type) {
      case "success":
        return "bg-green-100 text-green-800 border-green-200";
      case "warning":
        return "bg-amber-100 text-amber-800 border-amber-200";
      case "danger":
        return "bg-red-100 text-red-800 border-red-200";
      case "info":
      default:
        return "bg-blue-100 text-blue-800 border-blue-200";
    }
  };

  // Get appropriate icon based on value and type
  const getIcon = () => {
    if (icon) return icon;

    if (value) {
      return (
        <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
          <path
            fillRule="evenodd"
            d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
            clipRule="evenodd"
          />
        </svg>
      );
    } else {
      return (
        <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
          <path
            fillRule="evenodd"
            d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
            clipRule="evenodd"
          />
        </svg>
      );
    }
  };

  return (
    <span
      className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium border ${getBadgeStyle()} ${className}`}
      title={`${label}: ${value ? "Yes" : "No"}`}
    >
      {getIcon()}
      <span className="ml-1.5">{displayText}</span>
    </span>
  );
};

/**
 * JobSpecBadgeGroup Component
 *
 * A wrapper component for displaying multiple job specification badges in a group
 *
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Badge components
 * @param {string} props.title - Optional group title
 * @param {string} props.className - Additional CSS classes
 */
export const JobSpecBadgeGroup = ({ children, title, className = "" }) => {
  return (
    <div className={className}>
      {title && (
        <h4 className="text-sm font-medium text-gray-700 mb-2">{title}</h4>
      )}
      <div className="flex flex-wrap gap-2">{children}</div>
    </div>
  );
};

/**
 * Common job specification badges for easy use
 */
export const FloorConditionBadge = ({ floorIsGood }) => (
  <JobSpecBadge
    label="Floor Condition"
    value={floorIsGood}
    type={floorIsGood ? "success" : "warning"}
    trueText="Floor Is Good"
    falseText="Floor Needs Work"
    showWhenFalse={true}
  />
);

export const UnderlayBadge = ({ hasUnderlay }) => (
  <JobSpecBadge
    label="Underlay"
    value={hasUnderlay}
    type="info"
    trueText="Has Underlay"
    falseText="No Underlay"
    showWhenFalse={true}
  />
);

export const GrippersBadge = ({ hasGrippers }) => (
  <JobSpecBadge
    label="Grippers"
    value={hasGrippers}
    type="info"
    trueText="Has Grippers"
    falseText="No Grippers"
    showWhenFalse={true}
  />
);

export const FurnitureRemovalBadge = ({ furnitureRemoval }) => (
  <JobSpecBadge
    label="Furniture Removal"
    value={furnitureRemoval}
    type="warning"
    trueText="Furniture Removal Required"
    showWhenFalse={false}
  />
);

export const ConcreteBadge = ({ concrete }) => (
  <JobSpecBadge
    label="Concrete"
    value={concrete}
    type="info"
    trueText="Concrete Floor"
    showWhenFalse={false}
  />
);

export const OldFlooringBadge = ({ oldFlooringRemoved }) => (
  <JobSpecBadge
    label="Old Flooring"
    value={oldFlooringRemoved}
    type="warning"
    trueText="Old Flooring Removed"
    falseText="Old Flooring Present"
    showWhenFalse={true}
  />
);

export default JobSpecBadge;
